import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Radio, Button, Alert, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { clientBookingsApi } from '../../api/clientBookingsApi';
import { classifyCancelError } from '../../api/clientBookingsApi';
import { useBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { CANCELLATION_REASONS } from '../../types/client';
import { HeaderBar } from '../../components/client/ui/HeaderBar';

type SubmitState = 'idle' | 'success' | 'deadline' | 'error' | 'offline';

export default function CancellationPage() {
  const { t } = useTranslation('customer');
  const { id: uuid } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const haptic = useHaptic();
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  const schema = z.object({
    reason: z.enum(CANCELLATION_REASONS, {
      errorMap: () => ({ message: t('cancellation.reasonRequired') }),
    }),
  });

  type FormValues = z.infer<typeof schema>;

  const goBack = useCallback(() => navigate(-1), [navigate]);
  useBackButton(goBack);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: undefined },
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!uuid) return;
      setSubmitState('idle');
      try {
        await clientBookingsApi.cancelBooking(uuid, { reason: values.reason });
        haptic.notifySuccess();
        void message.success(t('cancellation.successMessage'));
        navigate('/');
      } catch (err) {
        const classified = classifyCancelError(err);
        if (classified.kind === 'deadline') {
          setSubmitState('deadline');
        } else if (classified.kind === 'offline' || classified.kind === 'network') {
          setSubmitState('offline');
        } else {
          haptic.notifyError();
          setSubmitState('error');
        }
      }
    },
    [uuid, haptic, navigate, t],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <HeaderBar title={t('cancellation.title')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {submitState === 'deadline' && (
          <Alert
            type="warning"
            message={t('cancellation.deadlineWarning')}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        {submitState === 'offline' && (
          <Alert
            type="error"
            message={t('cancellation.offlineError')}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        {submitState === 'error' && (
          <Alert
            type="error"
            message={t('cancellation.genericError')}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('cancellation.reasonHeading')}
          </Typography.Text>

          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}
              >
                {CANCELLATION_REASONS.map((r) => (
                  <Radio key={r} value={r}>
                    {t(`cancellation.reasons.${r}`)}
                  </Radio>
                ))}
              </Radio.Group>
            )}
          />
          {errors.reason && (
            <Typography.Text
              type="danger"
              style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
            >
              {errors.reason.message}
            </Typography.Text>
          )}

          <Button
            type="primary"
            danger
            htmlType="submit"
            block
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{ marginTop: 16 }}
          >
            {t('cancellation.submitBtn')}
          </Button>
        </form>
      </div>
    </div>
  );
}
